import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { X, Shield, Building, Users, FileText, Settings, Activity, UserPlus, Clipboard, BarChart3 } from 'lucide-react';

const iconMap = {
  shield: Shield,
  building: Building,
  users: Users,
  'file-text': FileText,
  settings: Settings,
  activity: Activity,
  'user-plus': UserPlus,
  clipboard: Clipboard,
  chart: BarChart3
};

const Sidebar = ({ navItems = [], isOpen, onClose, user }) => {
  const location = useLocation();
  const { user: authUser } = useAuth();
  const currentUser = user || authUser;

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || FileText;
    return IconComponent;
  };

  // Safety check for navItems
  const safeNavItems = Array.isArray(navItems) ? navItems : [];
  
  const groupedItems = safeNavItems.reduce((acc, item) => {
    const section = item.section || 'main';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  const sectionTitles = {
    main: 'Dashboard',
    admin: 'Administration',
    config: 'Configuration'
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-gradient-to-br from-slate-100 via-blue-100/80 to-indigo-100/90 border-r border-slate-300 shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:fixed
      `}>
        <div className="flex items-center justify-between h-20 pl-0 pr-1 border-b border-slate-300/60">
          <div className="flex items-center w-full -ml-6">
            <img src="/sidebar-logo.webp" alt="CANDIGATE" className="h-16 w-full object-contain" />
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md text-slate-600 hover:text-slate-800 hover:bg-slate-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 mt-6 px-3 overflow-y-auto">
          {Object.entries(groupedItems).map(([section, items]) => (
            <div key={section} className="mb-8">
              <h3 className="px-3 text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
                {sectionTitles[section]}
              </h3>
              <div className="space-y-1">
                {items.map((item) => {
                  const Icon = getIcon(item.icon);
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={onClose}
                      className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200
                        ${isActive 
                          ? 'bg-cobalt-500 text-white shadow-md shadow-cobalt-500/30' 
                          : 'text-slate-700 hover:bg-white/60 hover:text-cobalt-600'
                        }
                      `}
                    >
                      <Icon className={`
                        mr-3 h-5 w-5 flex-shrink-0
                        ${isActive ? 'text-white' : 'text-slate-600 group-hover:text-cobalt-500'}
                      `} />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User info */}
        {currentUser && (
          <div className="flex-shrink-0 p-4 border-t border-slate-300/60 bg-slate-200/40">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-cobalt-200 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-cobalt-700">
                    {currentUser.displayName?.[0] || currentUser.email?.[0] || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {currentUser.displayName || currentUser.email?.split('@')[0]}
                </p>
                <p className="text-xs text-slate-600 capitalize">
                  {currentUser.role?.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;